import React, { useMemo, useState } from "react";
import {
    Badge,
    Box,
    Button,
    Checkbox,
    CheckboxGroup,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    HStack,
    Icon,
    IconButton,
    Input,
    NumberInput,
    NumberInputField,
    RangeSlider,
    RangeSliderFilledTrack,
    RangeSliderThumb,
    RangeSliderTrack,
    Select,
    Stack,
    Text,
    VStack,
} from "@chakra-ui/react";
import {
    FiCheckCircle,
    FiChevronDown,
    FiChevronUp,
    FiFilter,
    FiRefreshCw,
    FiSearch,
    FiSliders,
    FiTruck,
    FiX,
} from "react-icons/fi";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

const CheckCircleIcon = FiCheckCircle as unknown as React.ElementType;
const ChevronDownIcon = FiChevronDown as unknown as React.ElementType;
const ChevronUpIcon = FiChevronUp as unknown as React.ElementType;
const FilterIcon = FiFilter as unknown as React.ElementType;
const RefreshIcon = FiRefreshCw as unknown as React.ElementType;
const SearchIcon = FiSearch as unknown as React.ElementType;
const SlidersIcon = FiSliders as unknown as React.ElementType;
const TruckIcon = FiTruck as unknown as React.ElementType;
const CloseIcon = FiX as unknown as React.ElementType;
const cardsBorderRadii = "8px";

export type SortOption =
    | "recommended"
    | "priceLowHigh"
    | "priceHighLow"
    | "discountHighLow"
    | "newest";

export type BusinessListingFilters = {
    search: string;
    categories: string[];
    priceRange: [number, number];
    minDiscount: number;
    deliveryEligibleOnly: boolean;
    availableOnly: boolean;
    location: string;
    sortBy: SortOption;
};

export type BusinessListingFilterOption = {
    value: string;
    label: string;
    count?: number;
};

export type ListingFilterType =
    | "products"
    | "accommodations"
    | "carHire"
    | "trips"
    | "hikes";

type FilterConfig = {
    searchLabel: string;
    searchPlaceholder: string;
    priceLabel: string;
    discountLabel: string;
    locationLabel: string;
    locationPlaceholder: string;
    categoryLabel: string;
    availableLabel: string;
    sortNewestLabel: string;
    showPrice: boolean;
    showDiscount: boolean;
    showDiscountShortcuts: boolean;
    showDelivery: boolean;
    showAvailability: boolean;
    showLocation: boolean;
    showCategories: boolean;
    showSort: boolean;
    showDiscountSort: boolean;
};

type Props = {
    filters: BusinessListingFilters;
    categoryOptions: BusinessListingFilterOption[];
    minPrice: number;
    maxPrice: number;
    totalItems: number;
    filteredItems: number;
    onFiltersChange: (filters: BusinessListingFilters) => void;
    onResetFilters: () => void;
    onClose?: () => void;
    showCloseButton?: boolean;
    listingType?: ListingFilterType;
};

const DEFAULT_VISIBLE_CATEGORY_COUNT = 6;

const formatMoney = (value: number) => {
    return `MWK ${Math.round(value || 0).toLocaleString()}`;
};

const clampPriceRange = (
    range: [number, number],
    minPrice: number,
    maxPrice: number
): [number, number] => {
    const start = Math.max(minPrice, Math.min(range[0], maxPrice));
    const end = Math.max(start, Math.min(range[1], maxPrice));

    return [start, end];
};

const normaliseListingType = (listingType?: ListingFilterType): ListingFilterType => {
    if (listingType === "hikes") return "trips";

    return listingType || "products";
};

const getFilterConfig = (listingType?: ListingFilterType): FilterConfig => {
    const type = normaliseListingType(listingType);

    if (type === "trips") {
        return {
            searchLabel: "Search trips",
            searchPlaceholder: "Search route, pickup, destination...",
            priceLabel: "Seat price range",
            discountLabel: "Discount",
            locationLabel: "Route / location",
            locationPlaceholder: "Search pickup, destination, city...",
            categoryLabel: "Vehicle / trip type",
            availableLabel: "Seats available only",
            sortNewestLabel: "Soonest trips",
            showPrice: true,
            showDiscount: false,
            showDiscountShortcuts: false,
            showDelivery: false,
            showAvailability: true,
            showLocation: true,
            showCategories: true,
            showSort: true,
            showDiscountSort: false,
        };
    }

    if (type === "accommodations") {
        return {
            searchLabel: "Search accommodation",
            searchPlaceholder: "Search hotels, stays, rooms...",
            priceLabel: "Price per night",
            discountLabel: "Discount",
            locationLabel: "Location",
            locationPlaceholder: "City, area, landmark...",
            categoryLabel: "Accommodation type",
            availableLabel: "Available stays only",
            sortNewestLabel: "Newest",
            showPrice: true,
            showDiscount: true,
            showDiscountShortcuts: true,
            showDelivery: false,
            showAvailability: true,
            showLocation: true,
            showCategories: true,
            showSort: true,
            showDiscountSort: true,
        };
    }

    if (type === "carHire") {
        return {
            searchLabel: "Search car hire",
            searchPlaceholder: "Search cars, vehicle type, location...",
            priceLabel: "Hire price range",
            discountLabel: "Discount",
            locationLabel: "Pickup location",
            locationPlaceholder: "City, area, pickup point...",
            categoryLabel: "Vehicle type",
            availableLabel: "Available cars only",
            sortNewestLabel: "Newest",
            showPrice: true,
            showDiscount: false,
            showDiscountShortcuts: false,
            showDelivery: false,
            showAvailability: true,
            showLocation: true,
            showCategories: true,
            showSort: true,
            showDiscountSort: false,
        };
    }

    return {
        searchLabel: "Search products",
        searchPlaceholder: "Search products, brands, shops...",
        priceLabel: "Price range",
        discountLabel: "Discount",
        locationLabel: "Location",
        locationPlaceholder: "City, area, pickup point...",
        categoryLabel: "Categories",
        availableLabel: "Available only",
        sortNewestLabel: "Newest",
        showPrice: true,
        showDiscount: true,
        showDiscountShortcuts: true,
        showDelivery: true,
        showAvailability: true,
        showLocation: true,
        showCategories: true,
        showSort: true,
        showDiscountSort: true,
    };
};

const ListingFilterSidebar: React.FC<Props> = ({
    filters,
    categoryOptions,
    minPrice,
    maxPrice,
    totalItems,
    filteredItems,
    onFiltersChange,
    onResetFilters,
    onClose,
    showCloseButton = false,
    listingType = "products",
}) => {
    const { theme } = useCustomTheme();
    const [showAllCategories, setShowAllCategories] = useState(false);

    const config = useMemo(() => getFilterConfig(listingType), [listingType]);

    const safeMinPrice = Number.isFinite(minPrice) ? minPrice : 0;
    const safeMaxPrice =
        Number.isFinite(maxPrice) && maxPrice > safeMinPrice
            ? maxPrice
            : safeMinPrice + 1;

    const selectedPriceRange = useMemo(
        () => clampPriceRange(filters.priceRange, safeMinPrice, safeMaxPrice),
        [filters.priceRange, safeMinPrice, safeMaxPrice]
    );

    const visibleCategoryOptions = useMemo(() => {
        if (showAllCategories) return categoryOptions;

        return categoryOptions.slice(0, DEFAULT_VISIBLE_CATEGORY_COUNT);
    }, [categoryOptions, showAllCategories]);

    const hiddenCategoryCount = Math.max(
        0,
        categoryOptions.length - DEFAULT_VISIBLE_CATEGORY_COUNT
    );

    const updateFilters = (partial: Partial<BusinessListingFilters>) => {
        onFiltersChange({
            ...filters,
            ...partial,
        });
    };

    const sortValue =
        !config.showDiscountSort && filters.sortBy === "discountHighLow"
            ? "recommended"
            : filters.sortBy;

    return (
        <Box
            bg={theme.surface}
            border={`1px solid ${theme.border}`}
            borderRadius={{ base: cardsBorderRadii, lg: cardsBorderRadii }}
            boxShadow={theme.shadowMd}
            overflow="hidden"
            w="100%"
            maxH={{
                base: showCloseButton ? "calc(92vh - 32px)" : "calc(100vh - 120px)",
                lg: "calc(100vh - 110px)",
            }}
            display="flex"
            flexDirection="column"
        >
            <Flex
                align="center"
                justify="space-between"
                px={5}
                py={4}
                bg={theme.surfaceAlt}
                borderBottom={`1px solid ${theme.border}`}
                flexShrink={0}
            >
                <HStack spacing={3}>
                    <Flex
                        boxSize="38px"
                        borderRadius={cardsBorderRadii}
                        align="center"
                        justify="center"
                        bg={theme.primarySoft}
                        color={theme.primary}
                    >
                        <Icon as={SlidersIcon} />
                    </Flex>

                    <Box>
                        <Text color={theme.text} fontWeight="900" lineHeight="1.1">
                            Filters
                        </Text>
                        <Text color={theme.textMuted} fontSize="xs" fontWeight="700">
                            {filteredItems} of {totalItems} shown
                        </Text>
                    </Box>
                </HStack>

                {showCloseButton && (
                    <IconButton
                        aria-label="Close filters"
                        icon={<Icon as={CloseIcon} />}
                        size="lg"
                        variant="ghost"
                        color={theme.textMuted}
                        onClick={onClose}
                    />
                )}
            </Flex>

            <Box
                overflowY="auto"
                flex="1"
                minH={0}
                sx={{
                    scrollbarWidth: "thin",
                    scrollbarColor: `${theme.borderStrong} transparent`,
                    "&::-webkit-scrollbar": {
                        width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                        background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        background: theme.borderStrong,
                        borderRadius:{cardsBorderRadii}
                    },
                }}
            >
                <VStack align="stretch" spacing={5} p={5}>
                    <FormControl>
                        <FormLabel
                            color={theme.text}
                            fontWeight="800"
                            fontSize="sm"
                            mb={2}
                        >
                            {config.searchLabel}
                        </FormLabel>

                        <HStack
                            bg={theme.background}
                            border={`1px solid ${theme.border}`}
                            borderRadius="6px"
                            px={3}
                            py={1}
                        >
                            <Icon as={SearchIcon} color={theme.textMuted} />

                            <Input
                                value={filters.search}
                                onChange={(event) =>
                                    updateFilters({ search: event.target.value })
                                }
                                placeholder={config.searchPlaceholder}
                                border="0"
                                px={1}
                                color={theme.text}
                                _placeholder={{ color: theme.textSubtle }}
                                _focus={{ boxShadow: "none" }}
                            />
                        </HStack>
                    </FormControl>

                    {config.showPrice && (
                        <>
                            <Divider borderColor={theme.border} />

                            <FormControl>
                                <Flex justify="space-between" align="center" mb={3}>
                                    <FormLabel
                                        color={theme.text}
                                        fontWeight="800"
                                        fontSize="sm"
                                        mb={0}
                                    >
                                        {config.priceLabel}
                                    </FormLabel>
                                </Flex>

                                <Box
                                    bg={theme.background}
                                    border={`1px solid ${theme.border}`}
                                    borderRadius="6px"
                                    p={4}
                                >
                                    <Flex justify="space-between" gap={3} mb={3}>
                                        <Box flex="1">
                                            <Text
                                                color={theme.textMuted}
                                                fontSize="xs"
                                                fontWeight="800"
                                                mb={1}
                                            >
                                                Min
                                            </Text>

                                            <NumberInput
                                                value={selectedPriceRange[0]}
                                                min={safeMinPrice}
                                                max={selectedPriceRange[1]}
                                                onChange={(_, value) => {
                                                    const nextMin = Number.isFinite(value)
                                                        ? value
                                                        : safeMinPrice;

                                                    updateFilters({
                                                        priceRange: [
                                                            Math.min(
                                                                nextMin,
                                                                selectedPriceRange[1]
                                                            ),
                                                            selectedPriceRange[1],
                                                        ],
                                                    });
                                                }}
                                            >
                                                <NumberInputField
                                                    borderRadius={cardsBorderRadii}
                                                    color={theme.text}
                                                    borderColor={theme.border}
                                                    fontSize="sm"
                                                />
                                            </NumberInput>
                                        </Box>

                                        <Box flex="1">
                                            <Text
                                                color={theme.textMuted}
                                                fontSize="xs"
                                                fontWeight="800"
                                                mb={1}
                                            >
                                                Max
                                            </Text>

                                            <NumberInput
                                                value={selectedPriceRange[1]}
                                                min={selectedPriceRange[0]}
                                                max={safeMaxPrice}
                                                onChange={(_, value) => {
                                                    const nextMax = Number.isFinite(value)
                                                        ? value
                                                        : safeMaxPrice;

                                                    updateFilters({
                                                        priceRange: [
                                                            selectedPriceRange[0],
                                                            Math.max(
                                                                nextMax,
                                                                selectedPriceRange[0]
                                                            ),
                                                        ],
                                                    });
                                                }}
                                            >
                                                <NumberInputField
                                                    borderRadius={cardsBorderRadii}
                                                    color={theme.text}
                                                    borderColor={theme.border}
                                                    fontSize="sm"
                                                />
                                            </NumberInput>
                                        </Box>
                                    </Flex>

                                    <RangeSlider
                                        aria-label={["Minimum price", "Maximum price"]}
                                        min={safeMinPrice}
                                        max={safeMaxPrice}
                                        step={Math.max(
                                            1,
                                            Math.ceil((safeMaxPrice - safeMinPrice) / 100)
                                        )}
                                        value={selectedPriceRange}
                                        onChange={(value) =>
                                            updateFilters({
                                                priceRange: [value[0], value[1]],
                                            })
                                        }
                                    >
                                        <RangeSliderTrack bg={theme.border}>
                                            <RangeSliderFilledTrack bg={theme.primary} />
                                        </RangeSliderTrack>
                                        <RangeSliderThumb index={0} boxSize={5} />
                                        <RangeSliderThumb index={1} boxSize={5} />
                                    </RangeSlider>

                                    <Flex justify="space-between" mt={3}>
                                        <Text
                                            color={theme.textMuted}
                                            fontSize="xs"
                                            fontWeight="800"
                                        >
                                            {formatMoney(selectedPriceRange[0])}
                                        </Text>
                                        <Text
                                            color={theme.textMuted}
                                            fontSize="xs"
                                            fontWeight="800"
                                        >
                                            {formatMoney(selectedPriceRange[1])}
                                        </Text>
                                    </Flex>
                                </Box>
                            </FormControl>
                        </>
                    )}

                    {config.showDiscount && (
                        <>
                            <Divider borderColor={theme.border} />

                            <FormControl>
                                <Flex justify="space-between" align="center" mb={3}>
                                    <FormLabel
                                        color={theme.text}
                                        fontWeight="800"
                                        fontSize="sm"
                                        mb={0}
                                    >
                                        {config.discountLabel}
                                    </FormLabel>

                                    <Text
                                        color={theme.primary}
                                        fontSize="sm"
                                        fontWeight="900"
                                    >
                                        {filters.minDiscount}%+
                                    </Text>
                                </Flex>

                                <RangeSlider
                                    aria-label={["Minimum discount", "Maximum discount"]}
                                    min={0}
                                    max={80}
                                    step={5}
                                    value={[filters.minDiscount, 80]}
                                    onChange={(value) =>
                                        updateFilters({
                                            minDiscount: value[0],
                                        })
                                    }
                                >
                                    <RangeSliderTrack bg={theme.border}>
                                        <RangeSliderFilledTrack bg={theme.primary} />
                                    </RangeSliderTrack>
                                    <RangeSliderThumb index={0} boxSize={5} />
                                    <RangeSliderThumb index={1} boxSize={0} opacity={0} />
                                </RangeSlider>

                                {config.showDiscountShortcuts && (
                                    <HStack mt={3} spacing={2} flexWrap="wrap">
                                        {[10, 20, 30, 50].map((discount) => (
                                            <Button
                                                key={discount}
                                                size="xs"
                                                borderRadius={cardsBorderRadii}
                                                variant={
                                                    filters.minDiscount === discount
                                                        ? "solid"
                                                        : "outline"
                                                }
                                                colorScheme="blue"
                                                onClick={() =>
                                                    updateFilters({
                                                        minDiscount: discount,
                                                    })
                                                }
                                            >
                                                {discount}% off
                                            </Button>
                                        ))}
                                    </HStack>
                                )}
                            </FormControl>
                        </>
                    )}

                    {(config.showDelivery || config.showAvailability) && (
                        <>
                            <Divider borderColor={theme.border} />

                            <VStack align="stretch" spacing={3}>
                                {config.showDelivery && (
                                    <Checkbox
                                        isChecked={filters.deliveryEligibleOnly}
                                        onChange={(event) =>
                                            updateFilters({
                                                deliveryEligibleOnly:
                                                    event.target.checked,
                                            })
                                        }
                                        colorScheme="green"
                                        color={theme.text}
                                        fontWeight="800"
                                    >
                                        <HStack>
                                            <Icon as={TruckIcon} />
                                            <Text fontSize="sm">
                                                Eligible for delivery
                                            </Text>
                                        </HStack>
                                    </Checkbox>
                                )}

                                {config.showAvailability && (
                                    <Checkbox
                                        isChecked={filters.availableOnly}
                                        onChange={(event) =>
                                            updateFilters({
                                                availableOnly: event.target.checked,
                                            })
                                        }
                                        colorScheme="green"
                                        color={theme.text}
                                        fontWeight="800"
                                    >
                                        <HStack>
                                            <Icon as={CheckCircleIcon} />
                                            <Text fontSize="sm">
                                                {config.availableLabel}
                                            </Text>
                                        </HStack>
                                    </Checkbox>
                                )}
                            </VStack>
                        </>
                    )}

                    {config.showLocation && (
                        <>
                            <Divider borderColor={theme.border} />

                            <FormControl>
                                <FormLabel
                                    color={theme.text}
                                    fontWeight="800"
                                    fontSize="sm"
                                    mb={2}
                                >
                                    {config.locationLabel}
                                </FormLabel>

                                <Input
                                    value={filters.location}
                                    onChange={(event) =>
                                        updateFilters({ location: event.target.value })
                                    }
                                    placeholder={config.locationPlaceholder}
                                    borderRadius="6px"
                                    bg={theme.background}
                                    borderColor={theme.border}
                                    color={theme.text}
                                    _placeholder={{ color: theme.textSubtle }}
                                />
                            </FormControl>
                        </>
                    )}

                    {config.showSort && (
                        <>
                            <Divider borderColor={theme.border} />

                            <FormControl>
                                <FormLabel
                                    color={theme.text}
                                    fontWeight="800"
                                    fontSize="sm"
                                    mb={2}
                                >
                                    Sort by
                                </FormLabel>

                                <Select
                                    value={sortValue}
                                    onChange={(event) =>
                                        updateFilters({
                                            sortBy: event.target.value as SortOption,
                                        })
                                    }
                                    borderRadius="6px"
                                    bg={theme.background}
                                    borderColor={theme.border}
                                    color={theme.text}
                                    fontWeight="700"
                                >
                                    <option value="recommended">Recommended</option>
                                    <option value="priceLowHigh">
                                        Price: Low to high
                                    </option>
                                    <option value="priceHighLow">
                                        Price: High to low
                                    </option>

                                    {config.showDiscountSort && (
                                        <option value="discountHighLow">
                                            Biggest discount
                                        </option>
                                    )}

                                    <option value="newest">
                                        {config.sortNewestLabel}
                                    </option>
                                </Select>
                            </FormControl>
                        </>
                    )}

                    {config.showCategories && (
                        <>
                            <Divider borderColor={theme.border} />

                            <FormControl>
                                <Flex justify="space-between" align="center" mb={3}>
                                    <FormLabel
                                        color={theme.text}
                                        fontWeight="800"
                                        fontSize="sm"
                                        mb={0}
                                    >
                                        {config.categoryLabel}
                                    </FormLabel>

                                    {!!filters.categories.length && (
                                        <Badge
                                            borderRadius={cardsBorderRadii}
                                            colorScheme="blue"
                                            px={2}
                                            py={0.5}
                                        >
                                            {filters.categories.length}
                                        </Badge>
                                    )}
                                </Flex>

                                {categoryOptions.length > 0 ? (
                                    <>
                                        <CheckboxGroup
                                            value={filters.categories}
                                            onChange={(values) =>
                                                updateFilters({
                                                    categories: values.map(String),
                                                })
                                            }
                                        >
                                            <Stack spacing={3}>
                                                {visibleCategoryOptions.map(
                                                    (category) => (
                                                        <Checkbox
                                                            key={category.value}
                                                            value={category.value}
                                                            colorScheme="blue"
                                                            color={theme.text}
                                                            fontWeight="700"
                                                        >
                                                            <Flex
                                                                align="center"
                                                                justify="space-between"
                                                                gap={3}
                                                                w="100%"
                                                            >
                                                                <Text fontSize="sm">
                                                                    {category.label}
                                                                </Text>

                                                                {typeof category.count ===
                                                                    "number" && (
                                                                        <Text
                                                                            as="span"
                                                                            color={
                                                                                theme.textMuted
                                                                            }
                                                                            fontSize="xs"
                                                                            fontWeight="800"
                                                                        >
                                                                            {category.count}
                                                                        </Text>
                                                                    )}
                                                            </Flex>
                                                        </Checkbox>
                                                    )
                                                )}
                                            </Stack>
                                        </CheckboxGroup>

                                        {hiddenCategoryCount > 0 && (
                                            <Button
                                                mt={4}
                                                size="sm"
                                                variant="ghost"
                                                borderRadius={cardsBorderRadii}
                                                rightIcon={
                                                    <Icon
                                                        as={
                                                            showAllCategories
                                                                ? ChevronUpIcon
                                                                : ChevronDownIcon
                                                        }
                                                    />
                                                }
                                                color={theme.primary}
                                                fontWeight="900"
                                                onClick={() =>
                                                    setShowAllCategories(
                                                        (current) => !current
                                                    )
                                                }
                                            >
                                                {showAllCategories
                                                    ? "Show fewer categories"
                                                    : `View ${hiddenCategoryCount} more categories`}
                                            </Button>
                                        )}

                                        {!!filters.categories.length && (
                                            <Button
                                                mt={2}
                                                size="sm"
                                                variant="outline"
                                                borderRadius={cardsBorderRadii}
                                                borderColor={theme.border}
                                                color={theme.text}
                                                onClick={() =>
                                                    updateFilters({
                                                        categories: [],
                                                    })
                                                }
                                            >
                                                Clear selected categories
                                            </Button>
                                        )}
                                    </>
                                ) : (
                                    <Text color={theme.textMuted} fontSize="sm">
                                        No categories available.
                                    </Text>
                                )}
                            </FormControl>
                        </>
                    )}

                    <Divider borderColor={theme.border} />

                    <Button
                        leftIcon={<Icon as={RefreshIcon} />}
                        onClick={onResetFilters}
                        variant="outline"
                        borderRadius={cardsBorderRadii}
                        color={theme.text}
                        borderColor={theme.border}
                        _hover={{
                            bg: theme.surfaceAlt,
                        }}
                    >
                        Reset filters
                    </Button>
                </VStack>

                <Box h="50px" />
            </Box>

            {showCloseButton && (
                <Box
                    p={4}
                    borderTop={`1px solid ${theme.border}`}
                    bg={theme.surface}
                    flexShrink={0}
                >
                    <Button
                        leftIcon={<Icon as={FilterIcon} />}
                        onClick={onClose}
                        borderRadius={cardsBorderRadii}
                        w="100%"
                        h="30px"
                        color={theme.textOnPrimary}
                        bgImage={theme.gradients.primaryButton}
                        _hover={{
                            bgImage: theme.gradients.primaryButtonHover,
                        }}
                    >
                        Show results
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default ListingFilterSidebar;